import { useEffect, useState } from "react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
} from "react-icons/fi";
import { IoMdSearch } from "react-icons/io";
import { useLocation, useNavigate } from "react-router-dom";
import { LIVE_URL } from "../../../config/config";
import useAuthToken from "../../../hooks/useAuthToken";
import styles from "./css/Feedback.module.scss";
import ProductTable from "./datatable/ProductTable";

const Feedback = () => {
  const { token } = useAuthToken();
  const navigate = useNavigate();
  const location = useLocation();

  // define state
  const [searchTerm, setSearchTerm] = useState(
    new URLSearchParams(location.search).get("firstname") || "",
  );
  const [feedbacks, setFeedbacks] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredFeedbacks, setFilteredFeedbacks] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [limit, setLimit] = useState(10);
  const [sortField, setSortField] = useState("id");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        setLoading(true);
        const queryParams = new URLSearchParams();
        if (searchTerm) {
          queryParams.set("firstname", searchTerm);
        }
        queryParams.set("page", currentPage);
        queryParams.set("pageSize", entriesPerPage);
        queryParams.set("sort_by", sortField);
        queryParams.set("order_by", sortOrder);
        queryParams.set("limit", limit);

        const response = await fetch(
          `${LIVE_URL}/admin/list/feedback?${queryParams}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const data = await response.json();

        if (response.ok && data.data && Array.isArray(data.data.feedback)) {
          setFeedbacks(data.data.feedback);
          setFilteredFeedbacks(data.data.feedback);
          setTotalPages(
            Math.ceil(data.data.pagination.totalCount / entriesPerPage),
          );
        } else {
          setFeedbacks([]);
          setFilteredFeedbacks([]);
          setTotalPages(1);
        }
      } catch (error) {
        console.error("Error fetching feedback:", error);
        setFeedbacks([]);
        setFilteredFeedbacks([]);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };

    if (!loading) {
      fetchFeedback();
    }
  }, [token, currentPage, searchTerm, entriesPerPage, sortField, sortOrder]);

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setLimit(newLimit);
    setEntriesPerPage(newLimit);
    setCurrentPage(1);
    setTotalPages(Math.ceil(filteredFeedbacks.length / newLimit) || 1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousTwo = () => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const handleNextTwo = () => {
    if (currentPage !== totalPages) {
      setCurrentPage(totalPages);
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    if (currentPage > 1) {
      pageNumbers.push(currentPage - 1);
    }
    pageNumbers.push(currentPage);
    if (currentPage < totalPages) {
      pageNumbers.push(currentPage + 1);
    }
    return pageNumbers;
  };

  const updateUrl = (search) => {
    const params = new URLSearchParams(location.search);
    if (search) {
      params.set("firstname", search);
    } else {
      params.delete("firstname");
    }
    navigate({ search: params.toString() });
  };

  useEffect(() => {
    updateUrl(searchTerm);
  }, [searchTerm]);

  return (
    <>
      <div className={`${styles.feedback}`}>
        <header>
          <h2>Feedback List Management</h2>
        </header>
        {/* entry section */}
        <section className={`${styles.section}`}>
          <div className={`${styles.user_entry}`}>
            <div className={`${styles.entries}`}>
              <label htmlFor="limit">Show</label>
              <select value={limit} onChange={handleLimitChange}>
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
              <label htmlFor="entries">entries</label>
            </div>
            <div className={`${styles.search}`}>
              {/* <label htmlFor="search">Search: </label> */}
              <IoMdSearch />
              <input
                type="search"
                id="search"
                value={searchTerm}
                placeholder="Search by name"
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* user information section */}
          <div className={`${styles.user_info}`}>
            <ProductTable
              apiEndpoint={`${LIVE_URL}/admin/list/feedback?`}
              feedbacks={filteredFeedbacks}
              setFeedbacks={setFeedbacks}
              rows={entriesPerPage}
              userName={searchTerm}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              sortField={sortField}
              sortOrder={sortOrder}
              setSortField={setSortField}
              setSortOrder={setSortOrder}
              loading={loading}
              setLoading={setLoading}
            />
          </div>

          {/* pagination section */}
          <div className={`${styles.pagination}`}>
            <button onClick={handlePreviousTwo} disabled={currentPage === 1}>
              <FiChevronsLeft />
            </button>
            <button onClick={handlePrevious} disabled={currentPage === 1}>
              <FiChevronLeft />
            </button>
            {getPageNumbers().map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={currentPage === page ? styles.active : ""}
                disabled={page > totalPages}
              >
                {page}
              </button>
            ))}
            <button onClick={handleNext} disabled={currentPage === totalPages}>
              <FiChevronRight />
            </button>
            <button
              onClick={handleNextTwo}
              disabled={
                currentPage === totalPages || currentPage === totalPages - 1
              }
            >
              <FiChevronsRight />
            </button>
          </div>
        </section>
      </div>
    </>
  );
};

export default Feedback;
